import { axiosClient } from '@/shared';
import { BaseResponse, ErrorResponse } from '@/shared/types';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { z } from 'zod';

/**
 * GTS 업로드 완료 정보 스키마
 *
 * - gts가 없으면: 일반 에셋 업로드 완료
 * - gts가 있으면: GTS Content로 설정 (detailType, meta)
 * - detailType: BE ContentDetailType 코드 (숫자)
 */
export const GtsUploadCompleteInfoSchema = z.object({
  /** Content 상세 타입 코드 (BE ContentDetailType) */
  detailType: z.number(),
  /** GTS 메타데이터 (선택) */
  meta: z.record(z.unknown()).optional(),
});

export type GtsUploadCompleteInfo = z.infer<typeof GtsUploadCompleteInfoSchema>;

/**
 * Asset Upload Complete 요청 타입
 *
 * 사용법:
 * - 일반 에셋 업로드 완료: { contentId, folderId }
 * - GTS 업로드 완료: { contentId, gts: { detailType: 105, meta: {...} } }
 */
export type AssetUploadCompleteRequest = {
  contentId: number;
  folderId?: number;
  /** GTS 업로드 완료 정보 (없으면 일반 에셋) */
  gts?: GtsUploadCompleteInfo;
};

/**
 * v2.0 Content Upload Complete API
 *
 * PATCH /v2.0/content/{contentId}/upload-complete
 *
 * contentId는 URL PathVariable로 전달
 */
export const assetUploadCompleteApi = async ({
  contentId,
  folderId,
  gts,
}: AssetUploadCompleteRequest) => {
  try {
    const response = await axiosClient.patch<BaseResponse>(
      `/v2.0/content/${contentId}/upload-complete`,
      { folderId, gts },
    );
    return response.data;
  } catch (error) {
    return Promise.reject(error);
  }
};

export const usePatchAssetUploadCompleteApi = () => {
  return useMutation<
    BaseResponse,
    AxiosError<ErrorResponse>,
    AssetUploadCompleteRequest
  >({
    mutationFn: assetUploadCompleteApi,
  });
};
